import { createI18nNamespace, type I18nKey, type I18nValues } from '../i18n';

export type PromptControlPlaneTranslate = (key: I18nKey | string, values?: I18nValues) => string;

const promptCopy = createI18nNamespace('modules.promptControlPlane');

export function createPromptControlPlaneCopy(t: PromptControlPlaneTranslate) {
  const key = (suffix: string) => promptCopy.key(suffix);
  const tx = (suffix: string, values?: I18nValues) => t(key(suffix), values);
  const moduleSuffix = (count: number) => count === 1 ? '' : 's';

  return {
    common: {
      unavailable: tx('common.unavailable'),
      none: tx('common.none'),
      yes: tx('common.yes'),
      no: tx('common.no'),
      defaultScope: tx('common.defaultScope'),
      knownChars: tx('common.knownChars'),
      charsRedacted: (count: string | number) => tx('common.charsRedacted', { count }),
      locked: tx('common.locked'),
      configurable: tx('common.configurable'),
      enabled: tx('common.enabled'),
      disabled: tx('common.disabled'),
      builtin: tx('common.builtin'),
      managed: tx('common.managed'),
      loadingLiveData: tx('common.loadingLiveData'),
      partialUnavailable: tx('common.partialUnavailable'),
    },
    header: {
      title: tx('header.title'),
      description: tx('header.description'),
      meta: {
        kind: tx('header.kindLabel'),
        kindValue: tx('header.kindValue'),
        source: tx('header.sourceLabel'),
        sourceValue: tx('header.sourceValue'),
        mutationPolicy: tx('header.mutationPolicyLabel'),
        mutationPolicyValue: tx('header.mutationPolicyValue'),
        plaintext: tx('header.plaintextLabel'),
        plaintextValue: tx('header.plaintextValue'),
      },
    },
    list: {
      refresh: tx('list.refresh'),
      refreshing: tx('list.refreshing'),
      rawJson: tx('list.rawJson'),
      createPrompt: tx('list.createPrompt'),
      filterSummary: (visible: number, total: number) => tx('list.filterSummary', { visible, total }),
      legacyHarness: tx('list.legacyHarness'),
      modulesTitle: tx('list.modulesTitle'),
      modulesDescription: tx('list.modulesDescription'),
      groupCount: (visible: number) => tx('list.groupCount', { visible }),
      groups: {
        coreTitle: tx('list.groups.coreTitle'),
        coreDescription: tx('list.groups.coreDescription'),
        builtinTitle: tx('list.groups.builtinTitle'),
        builtinDescription: tx('list.groups.builtinDescription'),
        consoleTitle: tx('list.groups.consoleTitle'),
        consoleDescription: tx('list.groups.consoleDescription'),
        emptyTitle: tx('list.groups.emptyTitle'),
        emptyDescription: tx('list.groups.emptyDescription'),
      },
      assignmentsTitle: tx('list.assignmentsTitle'),
      assignmentsDescription: tx('list.assignmentsDescription'),
      defaultAssignmentTitle: tx('list.defaultAssignmentTitle'),
      defaultAssignmentDescription: tx('list.defaultAssignmentDescription'),
      linearAssignmentTitle: tx('list.linearAssignmentTitle'),
      linearAssignmentDescription: tx('list.linearAssignmentDescription'),
      larkAssignmentTitle: tx('list.larkAssignmentTitle'),
      larkAssignmentDescription: tx('list.larkAssignmentDescription'),
      emptyTitle: tx('list.emptyTitle'),
      emptyDescription: tx('list.emptyDescription'),
      table: {
        module: tx('list.tableModule'),
        classification: tx('list.tableClassification'),
        source: tx('list.tableSource'),
        policy: tx('list.tablePolicy'),
        evidence: tx('list.tableEvidence'),
        details: tx('list.tableDetails'),
        actions: tx('list.tableActions'),
      },
      filters: {
        search: tx('list.filters.search'),
        searchPlaceholder: tx('list.filters.searchPlaceholder'),
        classification: tx('list.filters.classification'),
        state: tx('list.filters.state'),
        allTypes: tx('list.filters.allTypes'),
        allStates: tx('list.filters.allStates'),
        active: tx('list.filters.active'),
        inactive: tx('list.filters.inactive'),
        mutable: tx('list.filters.mutable'),
        readonly: tx('list.filters.readonly'),
        overrideAllowed: tx('list.filters.overrideAllowed'),
      },
      actions: {
        open: tx('list.actions.open'),
        edit: tx('list.actions.edit'),
        override: tx('list.actions.override'),
      },
      mutable: tx('list.mutable'),
      immutable: tx('list.immutable'),
      overrideAllowed: tx('list.overrideAllowed'),
      overrideLocked: tx('list.overrideLocked'),
      sharedMetadata: tx('list.sharedMetadata'),
      viewMetadata: tx('list.viewMetadata'),
      missingEvidence: tx('list.missingEvidence'),
      draftStatus: tx('list.draftStatus'),
      policyBlocked: tx('list.policyBlocked'),
    },
    status: {
      modules: tx('status.modules'),
      overrides: tx('status.overrides'),
      mutable: tx('status.mutable'),
      active: tx('status.active'),
      assignment: tx('status.assignment'),
      draft: tx('status.draft'),
      platformUi: tx('status.platformUi'),
      classification: tx('status.classification'),
      published: tx('status.published'),
      consumers: tx('status.consumers'),
      preview: tx('status.preview'),
      unpublished: tx('status.unpublished'),
      available: tx('status.available'),
      changed: tx('status.changed'),
      clean: tx('status.clean'),
      enabledCount: (enabled: number, total: number) => tx('status.enabledCount', { enabled, total }),
      builtinManagedEvidence: (builtin: number, managed: number) => tx('status.builtinManagedEvidence', { builtin, managed }),
      sameNameReplacement: tx('status.sameNameReplacement'),
      coreImmutableEvidence: tx('status.coreImmutableEvidence'),
      liveRows: tx('status.liveRows'),
      draftAssignmentView: tx('status.draftAssignmentView'),
      revision: (revision: string) => tx('status.revision', { revision }),
      realPlatformsOnly: tx('status.realPlatformsOnly'),
      artifactVersions: tx('status.artifactVersions'),
      behindLatest: (count: number) => tx('status.behindLatest', { count }),
      promptRedacted: tx('status.promptRedacted'),
    },
    detail: {
      eyebrow: tx('detail.eyebrow'),
      description: tx('detail.description'),
      refreshEvidence: tx('detail.refreshEvidence'),
      allPrompts: tx('detail.allPrompts'),
      tabs: {
        overview: tx('detail.overview'),
        evidence: tx('detail.evidence'),
        history: tx('detail.history'),
        raw: tx('detail.raw'),
      },
      classificationTitle: tx('detail.classificationTitle'),
      classificationDescription: tx('detail.classificationDescription'),
      redactedPreviewTitle: tx('detail.redactedPreviewTitle'),
      redactedPreviewDescription: tx('detail.redactedPreviewDescription'),
      actionEvidenceTitle: tx('detail.actionEvidenceTitle'),
      actionEvidenceDescription: tx('detail.actionEvidenceDescription'),
      versionHistoryTitle: tx('detail.versionHistoryTitle'),
      versionHistoryDescription: tx('detail.versionHistoryDescription'),
      rawDescription: tx('detail.rawDescription'),
      contentTitle: tx('detail.contentTitle'),
      contentDescription: tx('detail.contentDescription'),
      editorTitle: tx('detail.editorTitle'),
      editorDescription: tx('detail.editorDescription'),
      redactedEditorLocked: tx('detail.redactedEditorLocked'),
      draftJsonTitle: tx('detail.draftJsonTitle'),
      draftJsonDescription: tx('detail.draftJsonDescription'),
      draftJsonAction: tx('detail.draftJsonAction'),
      configJsonAction: tx('detail.configJsonAction'),
      openEditorDrawer: tx('detail.openEditorDrawer'),
      viewMarkdownDrawer: tx('detail.viewMarkdownDrawer'),
      openActionDrawer: tx('detail.openActionDrawer'),
      builtinDefaultTitle: tx('detail.builtinDefaultTitle'),
      managedOverrideTitle: tx('detail.managedOverrideTitle'),
      managedContentTitle: tx('detail.managedContentTitle'),
      usageTitle: tx('detail.usageTitle'),
      usageDescription: tx('detail.usageDescription'),
      moduleId: tx('detail.moduleId'),
      name: tx('detail.name'),
      status: tx('detail.status'),
      overrideTarget: tx('detail.overrideTarget'),
      versions: tx('detail.versions'),
      consumer: tx('detail.consumer'),
      platform: tx('detail.platform'),
      referenced: tx('detail.referenced'),
      runtime: tx('detail.runtime'),
      actionEvidence: tx('detail.actionEvidence'),
      noConsumersTitle: tx('detail.noConsumersTitle'),
      noConsumersDescription: tx('detail.noConsumersDescription'),
      currentLatest: (current: string | number, latest: string | number) => tx('detail.currentLatest', { current, latest }),
      runtimeVersion: (version: string | number) => tx('detail.runtimeVersion', { version }),
      rollbackBuiltinSuffix: tx('detail.rollbackBuiltinSuffix'),
      previewContent: tx('detail.previewContent'),
      previewValidation: tx('detail.previewValidation'),
      previewRevision: tx('detail.previewRevision'),
      markdown: tx('detail.markdown'),
      priority: tx('detail.priority'),
      conditionsTitle: tx('detail.conditionsTitle'),
      platformConditions: tx('detail.platformConditions'),
      agentConditions: tx('detail.agentConditions'),
      markdownPreview: tx('detail.markdownPreview'),
      editorDraftState: tx('detail.editorDraftState'),
      editorDirty: tx('detail.editorDirty'),
      editorClean: tx('detail.editorClean'),
      discardDraft: tx('detail.discardDraft'),
      saveDraftPreview: tx('detail.saveDraftPreview'),
      archivePreview: tx('detail.archivePreview'),
      deletePreview: tx('detail.deletePreview'),
      openPersonaConfig: tx('detail.openPersonaConfig'),
      actions: {
        createOverride: tx('detail.actions.createOverride'),
        createVersion: tx('detail.actions.createVersion'),
        publish: tx('detail.actions.publish'),
        rolloutAll: tx('detail.actions.rolloutAll'),
        rolloutSelected: tx('detail.actions.rolloutSelected'),
        rollback: tx('detail.actions.rollback'),
        rollbackBuiltinDefault: tx('detail.actions.rollbackBuiltinDefault'),
        destructive: tx('detail.actions.destructive'),
        apply: tx('detail.actions.apply'),
      },
    },
    assignments: {
      eyebrow: tx('assignments.eyebrow'),
      titleSuffix: tx('assignments.titleSuffix'),
      description: tx('assignments.description'),
      refresh: tx('assignments.refresh'),
      refreshing: tx('assignments.refreshing'),
      platform: tx('assignments.platform'),
      defaultPersona: tx('assignments.defaultPersona'),
      tabs: {
        overview: tx('assignments.overview'),
        modules: tx('assignments.modules'),
        editPreview: tx('assignments.editPreview'),
        rawJson: tx('assignments.rawJson'),
      },
      persona: tx('assignments.persona'),
      scope: tx('assignments.scope'),
      source: tx('assignments.source'),
      defaultScope: tx('assignments.defaultScope'),
      platformScope: (platform: string) => tx('assignments.platformScope', { platform }),
      sourceTitle: tx('assignments.sourceTitle'),
      sourceDescription: tx('assignments.sourceDescription'),
      sourceState: tx('assignments.sourceState'),
      draftChanged: tx('assignments.draftChanged'),
      mutationTitle: tx('assignments.mutationTitle'),
      mutationDescription: tx('assignments.mutationDescription'),
      openEditPreview: tx('assignments.openEditPreview'),
      modulesTitle: tx('assignments.modulesTitle'),
      modulesDescription: tx('assignments.modulesDescription'),
      edit: {
        title: tx('assignments.edit.title'),
        description: tx('assignments.edit.description'),
        reset: tx('assignments.edit.reset'),
        noPreviewTitle: tx('assignments.edit.noPreviewTitle'),
        noPreviewDescription: tx('assignments.edit.noPreviewDescription'),
        saveDraft: tx('assignments.edit.saveDraft'),
        enableAction: tx('assignments.edit.enableAction'),
        disableAction: tx('assignments.edit.disableAction'),
        actionTitle: (action: string, module: string) => tx('assignments.edit.actionTitle', { action, module }),
        previewEnable: tx('assignments.edit.previewEnable'),
        previewDisable: tx('assignments.edit.previewDisable'),
      },
      sourceStates: {
        baseline: tx('assignments.sourceStates.baseline'),
        inheritedBaseline: tx('assignments.sourceStates.inheritedBaseline'),
        platformOverride: tx('assignments.sourceStates.platformOverride'),
        platformAssignment: tx('assignments.sourceStates.platformAssignment'),
      },
      table: {
        module: tx('assignments.table.module'),
        origin: tx('assignments.table.origin'),
        enabled: tx('assignments.table.enabled'),
        assignmentState: tx('assignments.table.assignmentState'),
        evidence: tx('assignments.table.evidence'),
        emptyTitle: tx('assignments.table.emptyTitle'),
        emptyDescription: tx('assignments.table.emptyDescription'),
        inheritedBaseline: tx('assignments.table.inheritedBaseline'),
        platformOverride: tx('assignments.table.platformOverride'),
        baseline: tx('assignments.table.baseline'),
      },
    },
    actions: {
      applying: tx('actions.applying'),
      applied: tx('actions.applied'),
      unavailable: tx('actions.unavailable'),
      overrideCreatedOpen: tx('actions.overrideCreatedOpen'),
      overrideCreatedRefresh: tx('actions.overrideCreatedRefresh'),
      applyingAssignment: tx('actions.applyingAssignment'),
      assignmentSaved: tx('actions.assignmentSaved'),
      confirmationRequired: tx('actions.confirmationRequired'),
      assignmentConfirmationRequired: tx('actions.assignmentConfirmationRequired'),
      result: tx('actions.result'),
    },
    lifecycle: {
      createManaged: tx('lifecycle.createManaged'),
      createDescription: tx('lifecycle.createDescription'),
      createEvidence: tx('lifecycle.createEvidence'),
      saveDraft: tx('lifecycle.saveDraft'),
      saveDraftEvidence: tx('lifecycle.saveDraftEvidence'),
      archive: tx('lifecycle.archive'),
      delete: tx('lifecycle.delete'),
      destructivePreview: tx('lifecycle.destructivePreview'),
      name: tx('lifecycle.name'),
      displayName: tx('lifecycle.displayName'),
      description: tx('lifecycle.description'),
      markdown: tx('lifecycle.markdown'),
      priority: tx('lifecycle.priority'),
      previewCreate: tx('lifecycle.previewCreate'),
      createAndSeed: tx('lifecycle.createAndSeed'),
    },
    previews: {
      method: tx('previews.method'),
      endpoint: tx('previews.endpoint'),
      unavailableContractGap: tx('previews.unavailableContractGap'),
      selected: (count: number) => tx('previews.selected', { count }),
      selectedConsumers: (count: number) => tx('previews.selectedConsumers', { count }),
      candidateCurrent: (persona: string, platform: string, current: string | number) => tx('previews.candidateCurrent', { persona, platform, current }),
    },
    confirmations: {
      reviewExactRequest: tx('confirmations.reviewExactRequest'),
      reviewAssignmentScope: tx('confirmations.reviewAssignmentScope'),
    },
    contractGaps: {
      createOverrideUnavailable: tx('contractGaps.createOverrideUnavailable'),
      coreImmutable: tx('contractGaps.coreImmutable'),
      builtinDefaultReadOnly: tx('contractGaps.builtinDefaultReadOnly'),
      promptUsageReadOnly: tx('contractGaps.promptUsageReadOnly'),
      createManagedMissingFields: tx('contractGaps.createManagedMissingFields'),
      createVersionPreviewMissing: tx('contractGaps.createVersionPreviewMissing'),
      redactedEditorSourceUnsafe: tx('contractGaps.redactedEditorSourceUnsafe'),
      publishVersionMissing: tx('contractGaps.publishVersionMissing'),
      rolloutMissing: tx('contractGaps.rolloutMissing'),
      selectiveRolloutMissing: tx('contractGaps.selectiveRolloutMissing'),
      rollbackMissing: tx('contractGaps.rollbackMissing'),
      rollbackBuiltinMissing: tx('contractGaps.rollbackBuiltinMissing'),
      destructivePreviewMissing: tx('contractGaps.destructivePreviewMissing'),
      assignmentRowMissing: (module: string) => tx('contractGaps.assignmentRowMissing', { module }),
      assignmentLocked: (module: string) => tx('contractGaps.assignmentLocked', { module }),
      draftRevisionMissing: tx('contractGaps.draftRevisionMissing'),
      assignmentActionMissing: (module: string) => tx('contractGaps.assignmentActionMissing', { module }),
      platformRequired: tx('contractGaps.platformRequired'),
    },
    modelEvidence: {
      createOverride: tx('modelEvidence.createOverride'),
      createVersion: tx('modelEvidence.createVersion'),
      publish: (version: string | number) => tx('modelEvidence.publish', { version }),
      noVersion: tx('modelEvidence.noVersion'),
      rolloutAll: tx('modelEvidence.rolloutAll'),
      selectiveRollout: tx('modelEvidence.selectiveRollout'),
      rollback: tx('modelEvidence.rollback'),
      rollbackBuiltin: tx('modelEvidence.rollbackBuiltin'),
      assignmentPreview: (evidence: string, scope: string) => tx('modelEvidence.assignmentPreview', { evidence, scope }),
      resetPlatform: (platform: string) => tx('modelEvidence.resetPlatform', { platform }),
      coreReadable: tx('modelEvidence.coreReadable'),
      builtinDefault: tx('modelEvidence.builtinDefault'),
      managedOverride: tx('modelEvidence.managedOverride'),
      managedArtifact: tx('modelEvidence.managedArtifact'),
      runtimeBehind: tx('modelEvidence.runtimeBehind'),
      runtimeApplied: tx('modelEvidence.runtimeApplied'),
      runtimeVersion: (version: string | number) => tx('modelEvidence.runtimeVersion', { version }),
      runtimeUnavailable: tx('modelEvidence.runtimeUnavailable'),
      valid: tx('modelEvidence.valid'),
      validationIssues: (count: number) => tx('modelEvidence.validationIssues', { count, suffix: moduleSuffix(count) }),
      moduleAvailable: (version: string | number) => tx('modelEvidence.moduleAvailable', { version }),
      overrideArtifact: (artifactId: string, version: string | number) => tx('modelEvidence.overrideArtifact', { artifactId, version }),
      replacesBuiltin: (target: string) => tx('modelEvidence.replacesBuiltin', { target }),
      codeDefault: tx('modelEvidence.codeDefault'),
      inheritedPromptModules: tx('modelEvidence.inheritedPromptModules'),
      platformStorage: tx('modelEvidence.platformStorage'),
      storageEvidence: (storage: string, count: number, inheritance: string) => tx('modelEvidence.storageEvidence', { storage, count, suffix: moduleSuffix(count), inheritance }),
      direct: tx('modelEvidence.direct'),
      inherited: tx('modelEvidence.inherited'),
    },
  };
}

export type PromptControlPlaneCopy = ReturnType<typeof createPromptControlPlaneCopy>;
